import { useState } from 'react'
import { Icon } from '@iconify/react'
import { ScratchCard } from '@/components/ScratchCard/ScratchCard'
import {
  scratchProbabilityTotal,
  type ScratchCardConfig,
  type ScratchOutcome,
} from '@/lib/campaign-scratch-card'
import styles from './ScratchCardConfigEditor.module.css'

export function ScratchCardConfigEditor({
  value,
  onChange,
  disabled,
}: {
  value: ScratchCardConfig
  onChange: (value: ScratchCardConfig) => void
  disabled: boolean
}) {
  const [preview, setPreview] = useState(0)
  const [previewOpen, setPreviewOpen] = useState(false)
  const total = scratchProbabilityTotal(value.outcomes)
  const chance =
    value.outcomes
      .filter((outcome) => outcome.type !== 'none')
      .reduce((sum, outcome) => sum + Math.round(outcome.probability * 100), 0) / 100
  function update(index: number, changes: Partial<ScratchOutcome>) {
    onChange({
      ...value,
      outcomes: value.outcomes.map((outcome, position) =>
        position === index ? { ...outcome, ...changes } : outcome
      ),
    })
  }
  return (
    <div className={styles.editor}>
      <div className={styles.activation}>
        <label>
          <input
            type="checkbox"
            checked={value.enabled}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, enabled: event.target.checked })}
          />{' '}
          Ativar raspadinha nesta campanha
        </label>
        <p>
          Uma tentativa gratuita por usuário, sem exigir compra. Ativar ou editar os
          prêmios não renova tentativas já utilizadas.
        </p>
      </div>
      <div className={styles.heading}>
        <div>
          <h3>O que pode aparecer?</h3>
          <p>Combine números extras, cupons e resultados sem prêmio.</p>
        </div>
        <span>Até 20 resultados</span>
      </div>
      <div className={styles.outcomes}>
        {value.outcomes.map((outcome, index) => (
          <fieldset className={styles.outcome} disabled={disabled} key={index}>
            <legend>
              <Icon
                icon={
                  outcome.type === 'coupon'
                    ? 'mdi:ticket-percent-outline'
                    : outcome.type === 'lucky_number'
                      ? 'mdi:ticket-confirmation-outline'
                      : 'mdi:heart-outline'
                }
                aria-hidden
              />{' '}
              Resultado {index + 1}
            </legend>
            <label>
              Tipo de resultado
              <select
                value={outcome.type}
                onChange={(event) => {
                  const type = event.target.value as ScratchOutcome['type']
                  update(index, {
                    type,
                    quantity: type === 'lucky_number' ? 1 : null,
                    discount_percent: type === 'coupon' ? 10 : null,
                    label:
                      type === 'lucky_number'
                        ? 'Números extras'
                        : type === 'coupon'
                          ? 'Cupom de desconto'
                          : 'Não foi desta vez',
                  })
                }}
              >
                <option value="lucky_number">Números da sorte extras</option>
                <option value="coupon">Cupom de desconto</option>
                <option value="none">Sem prêmio</option>
              </select>
            </label>
            <label>
              Nome do resultado
              <input
                value={outcome.label}
                maxLength={120}
                required
                onChange={(event) => update(index, { label: event.target.value })}
              />
            </label>
            <div className={styles.values}>
              {outcome.type === 'lucky_number' && (
                <label>
                  Quantidade de números
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={1000}
                    step={1}
                    required
                    value={outcome.quantity ?? ''}
                    onChange={(event) =>
                      update(index, {
                        quantity:
                          event.target.value === '' ? null : Number(event.target.value),
                      })
                    }
                  />
                </label>
              )}
              {outcome.type === 'coupon' && (
                <label>
                  Desconto na próxima compra (%)
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0.01}
                    max={100}
                    step={0.01}
                    required
                    value={outcome.discount_percent ?? ''}
                    onChange={(event) =>
                      update(index, {
                        discount_percent:
                          event.target.value === '' ? null : Number(event.target.value),
                      })
                    }
                  />
                </label>
              )}
              <label>
                Probabilidade (%)
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.01}
                  required
                  value={Number.isNaN(outcome.probability) ? '' : outcome.probability}
                  onChange={(event) =>
                    update(index, {
                      probability:
                        event.target.value === '' ? NaN : Number(event.target.value),
                    })
                  }
                />
              </label>
            </div>
            <button
              type="button"
              className={styles.remove}
              disabled={disabled || value.outcomes.length === 1}
              onClick={() =>
                onChange({
                  ...value,
                  outcomes: value.outcomes.filter((_, position) => position !== index),
                })
              }
              aria-label={`Remover resultado ${index + 1}`}
            >
              <Icon icon="mdi:trash-can-outline" aria-hidden /> Remover
            </button>
          </fieldset>
        ))}
      </div>
      <button
        type="button"
        className={styles.add}
        disabled={disabled || value.outcomes.length >= 20}
        onClick={() =>
          onChange({
            ...value,
            outcomes: [
              ...value.outcomes,
              {
                type: 'lucky_number',
                label: 'Números extras',
                probability: 0,
                quantity: 1,
                discount_percent: null,
              },
            ],
          })
        }
      >
        <Icon icon="mdi:plus-circle-outline" aria-hidden /> Adicionar resultado
      </button>
      <div className={total === 100 ? styles.total : styles.invalidTotal} role="status">
        <strong>
          {Number.isFinite(total)
            ? total.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
            : '—'}
          % / 100%
        </strong>
        <span>
          {total === 100
            ? `${chance.toLocaleString('pt-BR')}% de chance de ganhar. Pronto para salvar.`
            : 'Ajuste as probabilidades até completar 100%.'}
        </span>
      </div>
      <p className={styles.help}>
        Cada tentativa sorteia um único resultado. 0% desativa a chance daquele resultado.
        As probabilidades valem por tentativa e não garantem uma quantidade exata de
        ganhadores. Os prêmios já entregues são preservados.
      </p>
      <label className={styles.message}>
        Mensagem para quem não ganhar
        <textarea
          rows={3}
          maxLength={2000}
          required
          disabled={disabled}
          value={value.no_prize_message}
          onChange={(event) =>
            onChange({ ...value, no_prize_message: event.target.value })
          }
        />
      </label>
      <details
        className={styles.preview}
        onToggle={(event) => setPreviewOpen(event.currentTarget.open)}
      >
        <summary>Experimentar a animação da raspadinha</summary>
        {previewOpen && (
          <>
            <p>Demonstração visual, sem sorteio ou entrega de prêmio.</p>
            <ScratchCard key={preview} onReveal={() => {}}>
              <Icon icon="mdi:star-four-points" aria-hidden />
              <strong>Surpresa revelada!</strong>
              <span>Assim o participante descobre o resultado.</span>
            </ScratchCard>
            <button
              type="button"
              className={styles.add}
              onClick={() => setPreview((current) => current + 1)}
            >
              Raspar novamente
            </button>
          </>
        )}
      </details>
    </div>
  )
}
